import React, { useCallback, useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { Pressable } from "react-native-gesture-handler";

import {
  type AttributeSet,
  type AttributeValue,
} from "@/schemas/attribute/AttributeSet";
import type { IoniconIconName } from "@/schemas/icons";
import { Spacings } from "@/styling/spacings";
import type { UserAttributeType } from "@/schemas/profile/UserAttributeSet";
import { themes } from "@/Components/Styling/themeConfigs";

const currentTheme = themes.morning;

type Props = {
  icon?: IoniconIconName;
  title?: string;
  attributeSet: AttributeSet<UserAttributeType>;
  onSelectionChange: (selectedValues: AttributeValue[]) => void;
  selectedValues?: AttributeValue[];
  displayIconLabel?: boolean;
};

// TODO: refactor with MultipleSelectionAttributeCard
function AttributeTagPicker({
  icon,
  title,
  attributeSet,
  onSelectionChange,
  selectedValues = [],
  displayIconLabel = true,
}: Props): React.JSX.Element {
  const isMultiSelect = attributeSet.isMultiSelect;
  const availableValues = Object.values(attributeSet.map);

  const selectedMap = useMemo(() => {
    const map = new Map();
    selectedValues.forEach((item) => map.set(item.id, item));
    return map;
  }, [selectedValues]);

  const isItemSelected = useCallback(
    (value: AttributeValue): boolean => {
      return selectedMap.has(value.id);
    },
    [selectedMap],
  );

  const handleSelect = useCallback(
    (value: AttributeValue): void => {
      let newSelected: AttributeValue[];
      const isSelected = selectedMap.has(value.id);

      if (isMultiSelect) {
        if (isSelected) {
          newSelected = selectedValues.filter((item) => item.id !== value.id);
        } else {
          newSelected = [...selectedValues, value];
        }
      } else {
        newSelected = isSelected ? [] : [value];
      }

      if (onSelectionChange) {
        onSelectionChange(newSelected);
      }
    },
    [selectedValues, isMultiSelect, onSelectionChange, selectedMap],
  );

  const Card = ({
    id,
    label,
    iconLabel,
  }: AttributeValue): React.JSX.Element => {
    const isSelected = isItemSelected({ id, label, iconLabel });

    return (
      <Pressable
        onPress={() => {
          handleSelect({ id, label, iconLabel });
        }}
      >
        <View style={[styles.card, isSelected && styles.selectedCard]}>
          {displayIconLabel && <Text style={styles.emoticon}>{iconLabel}</Text>}
          <Text style={[styles.label, isSelected && styles.selectedLabel]}>
            {label}
          </Text>
        </View>
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      {(icon || title) && (
        <View style={styles.sectionContainer}>
          {icon && <Ionicons name={icon} size={24} color="#01579b" />}
          {title && <Text style={styles.sectionTitle}>{title}</Text>}
        </View>
      )}
      <View style={styles.grid}>
        {availableValues.map((item, index) => (
          <Card key={index} {...item} />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: Spacings.s2,
  },
  sectionContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Spacings.s2,
  },
  sectionTitle: {
    fontSize: currentTheme.typography.h2.size.mobile,
    marginLeft: Spacings.s1,
    fontWeight: "bold",
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  card: {
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#f5f5f5",
    elevation: 2,
    flexDirection: "row",
    marginBottom: 5,
    marginRight: Spacings.s2,
    paddingHorizontal: Spacings.s3,
    paddingVertical: Spacings.s2,
    shadowColor: "#000",
    shadowOffset: { height: 1, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
  },
  selectedCard: {
    backgroundColor: "#e1f5fe",
    borderColor: "#01579b",
  },
  emoticon: {
    marginRight: Spacings.s2,
  },
  label: {
    fontSize: currentTheme.typography.body.size.mobile,
    fontWeight: "bold",
  },
  selectedLabel: {
    color: "#01579b",
  },
});

export default AttributeTagPicker;
